import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Shown instead of the crashed subtree, e.g. "WaChat Log". */
  label?: string
}

interface State {
  error: Error | null
}

/**
 * Contains a render failure to the panel it happened in.
 *
 * Without this, one unexpected API shape (the WhatsApp conversation rows return
 * `Sender` as an object, for instance) throws during render and React unmounts
 * the entire page — the user sees a blank screen instead of the record. The
 * legacy pages degraded per widget; this restores that behaviour.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`Render failed${this.props.label ? ` in ${this.props.label}` : ''}:`, error, info)
  }

  private reset = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children

    return (
      <div className="alert alert-error" role="alert">
        <strong>{this.props.label ? `${this.props.label} could not be displayed.` : 'This section could not be displayed.'}</strong>
        <div className="font12" style={{ marginTop: 4 }}>{error.message}</div>
        <button type="button" className="btn" style={{ marginTop: 8 }} onClick={this.reset}>
          Try again
        </button>
      </div>
    )
  }
}
